import React, { useState } from "react";
import PropTypes from "prop-types";

import Money from "../../../components/Money";
import { TransactionList } from "./TransactionList";
import { useHouseDispatch } from "../../../contexts/HouseContext";
import { playerStatus } from "../mobile/playerGroups";

import "./PlayerStats.css";

export function PlayerStats({ gameId, player, handleShowAddTransaction }) {
    const actions = useHouseDispatch();
    const [showTransactions, setShowTransactions] = useState(false);

    const transactions = player.transactions ?? [];
    const totals = player.totals ?? {};
    const playerHasTransactions = transactions.length > 0;

    const netClass = totals.net > 0 ? "is-up" : totals.net < 0 ? "is-down" : "is-zero";
    const status = playerStatus(player);

    const onAddTransaction = (e) => {
        e.stopPropagation();
        handleShowAddTransaction();
    };

    const onRemovePlayer = (e) => {
        e.stopPropagation();
        actions.removePlayer(player.id);
    };

    return (
        <>
            <div
                className={`player-row is-${status.tone}${status.cashedOut ? " is-cashed-out" : ""}${showTransactions ? " is-expanded" : ""}`}
                onClick={() => setShowTransactions((v) => !v)}
            >
                <div className="player-av" aria-hidden="true">
                    {initials(player.name)}
                </div>
                <div className="player-info">
                    <div className="player-name">{player.name}</div>
                    <div className="player-meta">
                        {transactionSummary(player, status)}
                    </div>
                </div>
                <div className="num col-buyin">
                    <Money amount={totals.buyIn ?? 0} />
                </div>
                <div className="num col-cashout">
                    <Money amount={totals.cashOut ?? 0} />
                </div>
                <div className={`num col-net ${netClass}`}>
                    <Money amount={totals.net ?? 0} signed={(totals.net ?? 0) !== 0} />
                </div>
                <div className="actions">
                    <button
                        className="row-act add"
                        title="Add transaction"
                        aria-label="Add transaction"
                        onClick={onAddTransaction}
                    >
                        +
                    </button>
                    <button
                        className="row-act del"
                        title="Remove player"
                        aria-label="Remove player"
                        disabled={playerHasTransactions}
                        onClick={onRemovePlayer}
                    >
                        ×
                    </button>
                </div>
            </div>
            {showTransactions && (
                <TransactionList
                    gameId={gameId}
                    playerId={player.id}
                    transactions={transactions}
                    handleShowAddTransaction={handleShowAddTransaction}
                />
            )}
        </>
    );
}

PlayerStats.propTypes = {
    gameId: PropTypes.string,
    player: PropTypes.object,
    handleShowAddTransaction: PropTypes.func,
};

function initials(name) {
    if (!name) return "·";
    const parts = name.trim().split(/\s+/);
    return (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
}

const PIP_CLASS = {
    owing: "is-warn",
    owed: "is-owed",
    settled: "is-up",
    active: "is-up",
    empty: "is-neutral",
};

// Status pip + summary for the row. `status` (from playerStatus) composes the
// cashed-out state with the money direction, so a cashed-out player who owes
// the table reads "… · cashed out · owes $X" rather than hiding it.
function transactionSummary(player, status) {
    const count = (player.transactions ?? []).length;
    const pip = PIP_CLASS[status.tone] ?? "is-neutral";
    const text =
        count === 0
            ? "No transactions yet"
            : `${count} ${count === 1 ? "transaction" : "transactions"} · ${status.label}`;

    return (
        <>
            <span className={`pip ${pip}`} aria-hidden="true" />
            {text}
        </>
    );
}
